import {type FC, useEffect, useMemo, useState} from 'react';

import LightboxLib from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Inline from 'yet-another-react-lightbox/plugins/inline';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

import type {Props} from './types';

import {LoadingSpinner} from '../../../../uikit/LoadingSpinner';
import {not} from '../../../../utils/conditionalUtils';
import {platform} from '../../../../utils/platform';
import {DocumentSlide} from './DocumentSlide';
import {HtmlContentSlide} from './HtmlContentSlide';
import {ImageSlide} from './ImageSlide';
import * as S from './styles';
import {getSlideType, isDocumentSrc} from './utils';

export const Lightbox: FC<Props> = ({
  getMountPoint,
  isInfinite = false,
  isInline = false,
  isOpen = true,
  onClose,
  slides,
  startIndex = 0,
}) => {
  const isSingleSlide = slides.length === 1;
  const [isZoomSupported, setIsZoomSupported] = useState(true);
  const [isDownloadSupported, setIsDownloadSupported] = useState(true);
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    setIndex(startIndex);
  }, [startIndex]);

  const onCloseCb = () => {
    setIndex(0);
    onClose?.();
  };

  const mountPoint = useMemo(() => {
    return getMountPoint?.() || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMountPoint, isOpen]);

  const plugins = useMemo(() => {
    const basePlugins = [Captions, Zoom, Fullscreen, Download];

    if (isInline) {
      basePlugins.push(Inline);
    }

    return basePlugins;
  }, [isInline]);

  return (
    <>
      <S.GlobalStyles />
      <LightboxLib
        carousel={{
          finite: !isInfinite && !isSingleSlide,
        }}
        close={onCloseCb}
        controller={{
          closeOnBackdropClick: true,
        }}
        index={index}
        on={{
          view: e => {
            const slide = slides[e.index]!;

            setIsZoomSupported(
              !isDocumentSrc(slide.src) && slide.type !== 'htmlContent',
            );

            /**
             * to be able to download files in webview we need to add support on native level
             * @see https://medium.com/@arradimohamed/downloading-files-from-a-wkwebview-has-never-been-easier-since-ios15-got-released-6b5c89cfa5f7
             */
            setIsDownloadSupported(
              not(platform.isMobileBrowser) && slide.type !== 'htmlContent',
            );
          },
        }}
        open={isOpen}
        plugins={plugins}
        portal={{
          root: mountPoint,
        }}
        render={{
          buttonDownload: isDownloadSupported ? undefined : () => null,
          buttonZoom: isZoomSupported ? undefined : () => null,
          iconClose: () => <S.CloseIcon />,
          iconDownload: () => <S.DownloadIcon />,
          iconEnterFullscreen: () => <S.EnterFullscreenIcon />,
          iconExitFullscreen: () => <S.ExitFullscreenIcon />,
          iconLoading: () => <LoadingSpinner size={50} />,
          iconNext: () =>
            isSingleSlide ? null : (
              <S.PrevNextIconContainer>
                <S.NextIcon />
              </S.PrevNextIconContainer>
            ),
          iconPrev: () =>
            isSingleSlide ? null : (
              <S.PrevNextIconContainer>
                <S.PrevIcon />
              </S.PrevNextIconContainer>
            ),
          iconZoomIn: () => <S.ZoomInIcon />,
          iconZoomOut: () => <S.ZoomOutIcon />,
          slide: ({slide, ...rest}) => {
            switch (slide.type) {
              case 'document':
                return (
                  <DocumentSlide
                    slide={slide}
                    {...rest}
                    // process only middle slide (by default lightbox renders 3 slides [-1, 0, 1])
                    isActive={rest.offset === 0}
                  />
                );
              case 'htmlContent':
                return <HtmlContentSlide slide={slide} {...rest} />;
              case 'image': {
                return <ImageSlide slide={slide} {...rest} />;
              }
              default:
                return null;
            }
          },
        }}
        // @ts-expect-error: Type 'keyof SlideTypes | undefined' is not assignable to type '"htmlContent"'.
        slides={slides.map(slide => {
          return {
            ...slide,
            disableCache: slide.disableCache || false,
            download: {
              filename: slide.fileName,
              url: slide.src,
            },
            type: slide.type || getSlideType(slide.src || ''),
          };
        })}
      />
    </>
  );
};
